import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import pLimit from "p-limit";
import { useToast } from "@hightouch/htui";
import {
  MAX_CHAT_UPLOAD_FILES,
  MAX_CHAT_UPLOAD_SIZE_BYTES,
} from "@hightouch/lib/llm/upload-limits";
import {
  isZipFileName,
  ZIP_UNPACK_LIMITS,
} from "@hightouch/lib/llm/zip-uploads/rules";
import {
  useInitiateChatFileUploads,
  useUploadChatFile,
} from "src/components/llm/hooks/use-upload-chat-file";
import {
  attachmentSize,
  base64ByteLength,
  validateAttachmentSelection,
} from "src/components/llm/utils/attachment-validation";
import {
  type ComposerFile,
  type UploadTarget,
  fitsInlineFallback,
  processFilesToComposerFiles,
} from "src/components/llm/utils/file-processing";
import { folderPathOf } from "src/components/llm/utils/folder-path";
import {
  loadZipDirectories,
  type ZipCheckAttachment,
  zipDirectoryOf,
  zipSelectionError,
} from "src/components/llm/utils/zip-attachment-check";

export type AttachmentUpload = {
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  folderPath?: string;
  loaded: number;
  total: number;
  status: "queued" | "uploading" | "failed";
  error?: string;
};

type AttachmentEntry = {
  id: string;
  file: ComposerFile;
  status: "ready" | AttachmentUpload["status"];
  loaded: number;
  uploadTotal?: number;
  error?: string;
  controller: AbortController;
  /**
   * Where this attachment's bytes go, minted with the rest of its selection.
   * Absent on an entry that already references storage, and on one whose mint
   * failed. The latter remains available for retry.
   */
  target?: UploadTarget;
  /** Inline budget claimed once the network refused this file's upload. */
  inlineBytes?: number;
};

// Ready entries holding bytes and no key go inline too, e.g. a failed send's restored attachments.
// They count at the bytes they carry, since `sizeBytes` is the size from before compression.
const inlineBytesOf = (entry: AttachmentEntry): number => {
  if (entry.inlineBytes !== undefined) return entry.inlineBytes;
  const { file } = entry;
  return entry.status === "ready" && file.base64Data && !file.s3Key
    ? base64ByteLength(file.base64Data)
    : 0;
};

const zipCheckAttachment = (file: ComposerFile): ZipCheckAttachment => ({
  fileName: file.fileName,
  sizeBytes: attachmentSize(file),
  zipDirectory: zipDirectoryOf(file),
});

const releaseAttachment = (entry: AttachmentEntry) => {
  entry.controller.abort();
  if (entry.file.previewUrl) URL.revokeObjectURL(entry.file.previewUrl);
};

export type FileContextType = {
  files: ComposerFile[];
  addFiles: (files: ComposerFile[]) => void;
  setFiles: (
    files: ComposerFile[] | ((prev: ComposerFile[]) => ComposerFile[]),
  ) => void;
  /** Empties the store without revoking ready files' preview URLs, for files the caller just sent. */
  handOffFiles: () => void;
  addProcessedFiles: (files: File[]) => Promise<ComposerFile[] | null>;
  addAttachments: (files: ComposerFile[]) => Promise<ComposerFile[] | null>;
  removeAttachment: (id: string) => void;
  removeAttachments: (ids: string[]) => void;
  retryAttachment: (id: string) => void | Promise<void>;
  isProcessingFiles: boolean;
  uploadsInFlight: Map<string, AttachmentUpload>;
  runWithProcessing: <T>(fn: () => Promise<T>) => Promise<T>;
};

export const FileContext = createContext<FileContextType | undefined>(
  undefined,
);

export type FileStoreOptions = {
  /** Upload images uncompressed, for chats where an upload becomes a library asset. */
  keepOriginalFiles?: boolean;
  /** Attachments allowed on one message. The server enforces the same cap for the chat. */
  maxFiles?: number;
};

/** The store reserves capacity before async work so concurrent selections cannot bypass limits. */
export const useFileStoreValue = ({
  keepOriginalFiles = false,
  maxFiles = MAX_CHAT_UPLOAD_FILES,
}: FileStoreOptions = {}): FileContextType => {
  const [entries, setEntries] = useState<AttachmentEntry[]>([]);
  const entriesRef = useRef(entries);
  const [pendingOperations, setPendingOperations] = useState(0);
  const queue = useRef(pLimit(4));
  const uploadFile = useUploadChatFile();
  const mintUploadTargets = useInitiateChatFileUploads();
  const { toast } = useToast();

  const publish = useCallback((next: AttachmentEntry[]) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  useEffect(
    () => () => {
      for (const entry of entriesRef.current) releaseAttachment(entry);
      entriesRef.current = [];
    },
    [],
  );

  const processEntry = async (
    entry: AttachmentEntry,
  ): Promise<ComposerFile | null> =>
    queue.current(async () => {
      if (entry.controller.signal.aborted) return null;
      const update = (patch: Partial<AttachmentEntry>) => {
        if (entry.controller.signal.aborted) return;
        publish(
          entriesRef.current.map((current) =>
            current.id === entry.id ? { ...current, ...patch } : current,
          ),
        );
      };
      update({ status: "uploading", error: undefined });
      try {
        let source = entry.file;
        let rawFile = source.file;
        if (!rawFile && entry.file.loadFile) {
          rawFile = await entry.file.loadFile(entry.controller.signal);
          if (entry.controller.signal.aborted) return null;
          const resolved = {
            ...entry.file,
            file: rawFile,
            sizeBytes: rawFile.size,
          };
          validateAttachmentSelection(
            entriesRef.current
              .filter((current) => current.id !== entry.id)
              .map((current) => current.file),
            [resolved],
            maxFiles,
          );
          source = resolved;
          update({ file: resolved });
        }
        if (!rawFile && entry.file.base64Data) {
          const bytes = Uint8Array.from(atob(entry.file.base64Data), (char) =>
            char.charCodeAt(0),
          );
          rawFile = new File([bytes], entry.file.fileName, {
            type: entry.file.mimeType,
          });
        }
        if (!rawFile)
          throw new Error(
            "File contents are unavailable. Remove this attachment and select it again.",
          );
        // Read from the live entry: a retry mints a fresh target after the
        // original expired, and `entry` is the stale closure copy.
        const target =
          entriesRef.current.find((current) => current.id === entry.id)
            ?.target ?? entry.target;
        if (!target) {
          throw new Error(
            "No upload target was minted for this attachment. Remove it and try again.",
          );
        }
        const [uploaded] = await processFilesToComposerFiles(
          [rawFile],
          uploadFile,
          MAX_CHAT_UPLOAD_SIZE_BYTES,
          {
            signal: entry.controller.signal,
            targets: [target],
            compressImages: !keepOriginalFiles,
            onFileProgress: (_, progress) =>
              update({ loaded: progress.loaded, uploadTotal: progress.total }),
            reserveInlineBytes: (bytes) => {
              const inUse = entriesRef.current
                .filter((current) => current.id !== entry.id)
                .reduce((sum, current) => sum + inlineBytesOf(current), 0);
              if (!fitsInlineFallback(bytes, inUse)) return false;
              update({ inlineBytes: bytes });
              return true;
            },
          },
        );
        if (!uploaded)
          throw new Error("The file could not be uploaded. Try again.");
        if (entry.controller.signal.aborted) {
          if (uploaded.previewUrl) URL.revokeObjectURL(uploaded.previewUrl);
          return null;
        }
        const file: ComposerFile = {
          ...uploaded,
          id: entry.id,
          sizeBytes: attachmentSize(source),
          contentImageId: entry.file.contentImageId,
          catalogAssetId: entry.file.catalogAssetId,
          folderPath: entry.file.folderPath,
        };
        update({ file, status: "ready" });
        return file;
      } catch (error) {
        if (entry.controller.signal.aborted) return null;
        const message =
          error instanceof Error
            ? error.message
            : "The upload failed. Try again.";
        update({ status: "failed", error: message, inlineBytes: undefined });
        toast({
          id: "file-upload-error",
          variant: "error",
          title: "Couldn't upload file",
          message,
        });
        return null;
      }
    });

  const validateSelection = (incoming: ComposerFile[]): boolean => {
    try {
      validateAttachmentSelection(
        entriesRef.current.map((entry) => entry.file),
        incoming,
        maxFiles,
      );
      return true;
    } catch (error) {
      toast({
        id: "file-attach-limit",
        variant: "warning",
        title: "Couldn't attach files",
        message:
          error instanceof Error
            ? error.message
            : "Choose fewer or smaller files.",
      });
      return false;
    }
  };

  const addAttachments = async (
    selection: ComposerFile[],
  ): Promise<ComposerFile[] | null> => {
    if (selection.length === 0) return [];
    if (!validateSelection(selection)) return null;
    // Only a selection with a zip waits on a read, so every other selection still reserves its capacity synchronously.
    if (selection.some((file) => file.file && isZipFileName(file.fileName))) {
      await loadZipDirectories(selection);
      // Other selections may have landed during the read.
      if (!validateSelection(selection)) return null;
    }
    const zipError = zipSelectionError({
      existing: entriesRef.current.map((entry) =>
        zipCheckAttachment(entry.file),
      ),
      incoming: selection.map(zipCheckAttachment),
      limits: { ...ZIP_UNPACK_LIMITS, maxMembers: maxFiles },
    });
    if (zipError) {
      toast({
        id: "file-attach-limit",
        variant: "warning",
        title: "Couldn't attach files",
        message: zipError,
      });
      return null;
    }
    const added: AttachmentEntry[] = selection.map((file) => {
      const id = crypto.randomUUID();
      return {
        id,
        file: { ...file, id, sizeBytes: attachmentSize(file) },
        status: file.s3Key || file.catalogAssetId ? "ready" : "queued",
        loaded: 0,
        controller: new AbortController(),
      };
    });
    publish([...entriesRef.current, ...added]);

    // One mint for the whole selection, before any bytes move. The server
    // applies the count and total limits here, so a selection it refuses
    // fails now rather than after a hundred transfers.
    const needTargets = added.filter((entry) => entry.status !== "ready");
    if (needTargets.length > 0) {
      try {
        const targets = await mintUploadTargets(
          needTargets.map((entry) => ({
            fileName: entry.file.fileName,
            // Absent for a library attachment, whose bytes are fetched only
            // once its upload is admitted.
            sizeBytes: attachmentSize(entry.file),
          })),
        );
        for (const [index, entry] of needTargets.entries()) {
          entry.target = targets[index];
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Couldn't prepare the upload. Try again.";
        toast({
          id: "file-attach-limit",
          variant: "warning",
          title: "Couldn't attach files",
          message,
        });
        for (const entry of needTargets) releaseAttachment(entry);
        publish(
          entriesRef.current.filter(
            (entry) => !needTargets.some((failed) => failed.id === entry.id),
          ),
        );
        return null;
      }
    }

    const results = await Promise.all(
      added.map((entry) =>
        entry.status === "ready" ? entry.file : processEntry(entry),
      ),
    );
    const successful = results.filter(
      (file): file is ComposerFile => file !== null,
    );
    return successful.length > 0 ? successful : null;
  };

  const addProcessedFiles = (files: File[]) =>
    addAttachments(
      files.map((file) => ({
        file,
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        folderPath: folderPathOf(file),
      })),
    );

  const setFiles = useCallback(
    (value: ComposerFile[] | ((prev: ComposerFile[]) => ComposerFile[])) => {
      const current = entriesRef.current;
      const ready = current
        .filter((entry) => entry.status === "ready")
        .map((entry) => entry.file);
      const next = typeof value === "function" ? value(ready) : value;
      try {
        validateAttachmentSelection(
          typeof value === "function"
            ? current
                .filter((entry) => entry.status !== "ready")
                .map((entry) => entry.file)
            : [],
          next,
          maxFiles,
        );
      } catch (error) {
        toast({
          id: "file-attach-limit",
          variant: "warning",
          title: "Couldn't attach files",
          message:
            error instanceof Error
              ? error.message
              : "Choose fewer or smaller files.",
        });
        return;
      }
      const nextEntries = next.map((file): AttachmentEntry => {
        const existing = current.find((entry) => entry.file === file);
        if (existing) return existing;
        const id = crypto.randomUUID();
        return {
          id,
          file: {
            ...file,
            id,
            ...(file.file
              ? { previewUrl: URL.createObjectURL(file.file) }
              : {}),
          },
          status: "ready",
          loaded: 0,
          controller: new AbortController(),
        };
      });
      // A replacement or clear cancels uploads; a functional removal preserves queued files.
      if (typeof value === "function")
        nextEntries.push(
          ...current.filter((entry) => entry.status !== "ready"),
        );
      const kept = new Set(nextEntries.map((entry) => entry.id));
      for (const entry of current)
        if (!kept.has(entry.id)) releaseAttachment(entry);
      publish(nextEntries);
    },
    [publish, toast, maxFiles],
  );

  const handOffFiles = useCallback(() => {
    // Sent messages keep rendering these previews, so only unsent entries are released.
    for (const entry of entriesRef.current)
      if (entry.status !== "ready") releaseAttachment(entry);
    publish([]);
  }, [publish]);

  const removeAttachments = (ids: string[]) => {
    const removed = new Set(ids);
    for (const entry of entriesRef.current) {
      if (removed.has(entry.id)) releaseAttachment(entry);
    }
    publish(entriesRef.current.filter((entry) => !removed.has(entry.id)));
  };

  const removeAttachment = (id: string) => removeAttachments([id]);

  const retryAttachment = async (id: string) => {
    const entry = entriesRef.current.find((candidate) => candidate.id === id);
    if (!entry || entry.status !== "failed") return;
    publish(
      entriesRef.current.map((candidate) =>
        candidate.id === id
          ? { ...candidate, status: "queued", loaded: 0, error: undefined }
          : candidate,
      ),
    );
    // A fresh target rather than the original: the first attempt may have
    // failed because the presigned URL expired, and re-PUTting to a dead URL
    // would fail the same way every time.
    try {
      const [target] = await mintUploadTargets([
        {
          fileName: entry.file.fileName,
          sizeBytes: attachmentSize(entry.file),
        },
      ]);
      entry.target = target;
      publish(
        entriesRef.current.map((candidate) =>
          candidate.id === id ? { ...candidate, target } : candidate,
        ),
      );
    } catch (error) {
      publish(
        entriesRef.current.map((candidate) =>
          candidate.id === id
            ? {
                ...candidate,
                status: "failed",
                error:
                  error instanceof Error
                    ? error.message
                    : "Couldn't prepare the upload. Try again.",
              }
            : candidate,
        ),
      );
      return;
    }
    await processEntry(entry);
  };

  const runWithProcessing = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      setPendingOperations((count) => count + 1);
      try {
        return await fn();
      } finally {
        setPendingOperations((count) => count - 1);
      }
    },
    [],
  );

  const files = useMemo(
    () =>
      entries
        .filter((entry) => entry.status === "ready")
        .map((entry) => entry.file),
    [entries],
  );
  const uploadsInFlight = new Map<string, AttachmentUpload>();
  for (const entry of entries) {
    if (entry.status !== "ready")
      uploadsInFlight.set(entry.id, {
        fileName: entry.file.fileName,
        mimeType: entry.file.mimeType,
        sizeBytes: attachmentSize(entry.file),
        folderPath: entry.file.folderPath ?? undefined,
        total: entry.uploadTotal ?? attachmentSize(entry.file) ?? 0,
        loaded: entry.loaded,
        status: entry.status,
        error: entry.error,
      });
  }
  return {
    files,
    setFiles,
    handOffFiles,
    addFiles: (incoming) => {
      void addAttachments(incoming);
    },
    addAttachments,
    addProcessedFiles,
    removeAttachment,
    removeAttachments,
    retryAttachment,
    uploadsInFlight,
    isProcessingFiles: pendingOperations > 0 || uploadsInFlight.size > 0,
    runWithProcessing,
  };
};

export const FileProvider = ({
  children,
  keepOriginalFiles,
  maxFiles,
}: FileStoreOptions & { children: ReactNode }) => {
  const value = useFileStoreValue({ keepOriginalFiles, maxFiles });

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

const defaultContext: FileContextType = {
  files: [],
  addFiles: () => {},
  setFiles: () => {},
  handOffFiles: () => {},
  addProcessedFiles: async () => null,
  addAttachments: async () => null,
  removeAttachment: () => {},
  removeAttachments: () => {},
  retryAttachment: () => {},
  isProcessingFiles: false,
  uploadsInFlight: new Map(),
  runWithProcessing: async (fn) => fn(),
};

/**
 * Reads the surrounding provider, degrading to an inert store when there isn't
 * one. Intended for code that only wants to *signal into* a provider it may or
 * may not be under — e.g. pickers calling `runWithProcessing` so a parent
 * composer can gate its Send button.
 *
 * Composers that own attachments must use `useComposerFileStore` instead: the
 * inert store silently swallows writes, so a composer built on this hook loses
 * every attachment when no provider happens to be mounted above it.
 */
export const useFiles = (): FileContextType => {
  const context = useContext(FileContext);
  return context ?? defaultContext;
};

export const useFilesStrict = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFilesStrict must be used within a FileProvider");
  }
  return context;
};
